import { ReadingCircle } from '../Types/ReadingCircle';
import { ReadingCircleInvitation } from '../Types/ReadingCircleInvitation';
import {
  ReadingCircleLeaderboard,
  ReadingCircleLeaderboardEntry,
} from '../Types/ReadingCircleLeaderboard';
import { ReadingCircleMembership } from '../Types/ReadingCircleMembership';
import {
  ReactionType,
  ReadingCircleSocialActivityEventReaction,
} from '../Types/ReadingCircleSocialActivityEventReaction';
import { SocialActivityEvent } from '../Types/SocialActivityEvent';
import { type User } from '../Types/User';
import { getApiInstance } from './Api';
import { mapUser } from './UserApi';

function mapReadingCircle(readingCircle: any): ReadingCircle {
  return {
    ...readingCircle,
    memberships: readingCircle.memberships?.map(mapReadingCircleMembership),
    createdAt: new Date(readingCircle.createdAt),
  };
}

function mapReadingCircleMembership(membership: any): ReadingCircleMembership {
  return {
    ...membership,
    user: membership.user ? mapUser(membership.user) : undefined,
    readingCircle: membership.readingCircle
      ? mapReadingCircle(membership.readingCircle)
      : undefined,
  };
}

function mapReadingCircleInvitation(invitation: any): ReadingCircleInvitation {
  return {
    ...invitation,
    readingCircle: invitation.readingCircle
      ? mapReadingCircle(invitation.readingCircle)
      : undefined,
    invitee: invitation.invitee ? mapUser(invitation.invitee) : undefined,
    issuer: invitation.issuer ? mapUser(invitation.issuer) : undefined,
    createdAt: invitation.createdAt
      ? new Date(invitation.createdAt)
      : undefined,
    updatedAt: invitation.updatedAt
      ? new Date(invitation.updatedAt)
      : undefined,
  };
}

function mapSocialActivityEvent(event: any): SocialActivityEvent {
  return {
    ...event,
    user: event.user ? mapUser(event.user) : undefined,
    createdAt: new Date(event.createdAt),
  };
}

function mapReadingCircleSocialActivityEventReaction(
  reaction: any,
): ReadingCircleSocialActivityEventReaction {
  return {
    ...reaction,
    user: reaction.user ? mapUser(reaction.user) : undefined,
    readingCircle: reaction.readingCircle
      ? mapReadingCircle(reaction.readingCircle)
      : undefined,
    event: reaction.event ? mapSocialActivityEvent(reaction.event) : undefined,
    createdAt: new Date(reaction.createdAt),
  };
}

function mapReadingCircleLeaderboard(
  leaderboard: any,
): ReadingCircleLeaderboard {
  return {
    ...leaderboard,
    entries: leaderboard.entries.map(mapReadingCircleLeaderboardEntry),
  };

  function mapReadingCircleLeaderboardEntry(
    entry: any,
  ): ReadingCircleLeaderboardEntry {
    return {
      ...entry,
      user: entry.user ? mapUser(entry.user) : undefined,
    };
  }
}

export class ReadingCircleApi {
  async getUserReadingCircles(): Promise<ReadingCircle[]> {
    const response = await getApiInstance().get('/circles');
    return response.data.map(mapReadingCircle);
  }

  async getReadingCircle(id: string): Promise<ReadingCircle> {
    const response = await getApiInstance().get(`/circles/${id}`);
    return mapReadingCircle(response.data);
  }

  async createReadingCircle(
    readingCircle: Partial<ReadingCircle>,
  ): Promise<ReadingCircle> {
    const response = await getApiInstance().post('/circles', readingCircle);
    return mapReadingCircle(response.data);
  }

  async updateReadingCircle(
    id: string,
    readingCircle: Partial<ReadingCircle>,
  ): Promise<ReadingCircle> {
    const response = await getApiInstance().put(
      `/circles/${id}`,
      readingCircle,
    );
    return mapReadingCircle(response.data);
  }

  async deleteReadingCircle(id: string): Promise<void> {
    await getApiInstance().delete(`/circles/${id}`);
  }

  async addStudentUserToReadingCircle(
    readingCircleId: string,
    studentUserId: string,
  ): Promise<ReadingCircleMembership> {
    const response = await getApiInstance().post(
      `/circles/${readingCircleId}/students/${studentUserId}`,
    );
    return mapReadingCircleMembership(response.data);
  }

  async removeUserFromReadingCircle(
    readingCircleId: string,
    userId: string,
  ): Promise<ReadingCircleMembership[]> {
    const response = await getApiInstance().delete(
      `/circles/${readingCircleId}/members/${userId}`,
    );
    return response.data.map(mapReadingCircleMembership);
  }

  async inviteToReadingCircle(
    readingCircleId: string,
    email: string,
  ): Promise<ReadingCircleInvitation> {
    const response = await getApiInstance().post(
      `/circles/${readingCircleId}/invitations`,
      {
        email,
      },
    );
    return mapReadingCircleInvitation(response.data);
  }

  async getPendingInvitations(
    readingCircleId: string,
  ): Promise<ReadingCircleInvitation[]> {
    const response = await getApiInstance().get(
      `/circles/${readingCircleId}/invitations`,
    );
    return response.data.map(mapReadingCircleInvitation);
  }

  async cancelInvitation(
    readingCircleId: string,
    invitationId: string,
  ): Promise<ReadingCircleInvitation> {
    const response = await getApiInstance().delete(
      `/circles/${readingCircleId}/invitations/${invitationId}`,
    );
    return mapReadingCircleInvitation(response.data);
  }

  async getInvitation(token: string): Promise<ReadingCircleInvitation> {
    const response = await getApiInstance().get(`/circles/invitation/${token}`);
    return mapReadingCircleInvitation(response.data);
  }

  async acceptInvitation(token: string): Promise<ReadingCircleInvitation> {
    const response = await getApiInstance().post(
      `/circles/invitation/${token}/accept`,
    );
    return mapReadingCircleInvitation(response.data);
  }

  async getReadingCircleSocialActivityEvents(
    readingCircleId: string,
    take: number,
    beforeTimestamp?: Date | undefined,
  ): Promise<SocialActivityEvent[]> {
    const response = await getApiInstance().get(
      `/circles/${readingCircleId}/events/?take=${take}&${
        beforeTimestamp
          ? `beforeTimestamp=${beforeTimestamp?.toISOString()}`
          : ''
      }`,
    );

    return response.data.map(mapSocialActivityEvent);
  }

  async getReadingCircleSocialActivityEventReactions(
    readingCircleId: string,
    eventId: string,
  ): Promise<ReadingCircleSocialActivityEventReaction[]> {
    const response = await getApiInstance().get(
      `/circles/${readingCircleId}/events/${eventId}/reactions`,
    );
    return response.data.map(mapReadingCircleSocialActivityEventReaction);
  }

  async addReadingCircleSocialActivityEventReaction(
    readingCircleId: string,
    eventId: string,
    reactionType: ReactionType,
  ): Promise<ReadingCircleSocialActivityEventReaction> {
    const response = await getApiInstance().post(
      `/circles/${readingCircleId}/events/${eventId}/reactions`,
      {
        type: reactionType,
      },
    );
    return mapReadingCircleSocialActivityEventReaction(response.data);
  }

  async removeReadingCircleSocialActivityEventReaction(
    readingCircleId: string,
    eventId: string,
  ): Promise<void> {
    await getApiInstance().delete(
      `/circles/${readingCircleId}/events/${eventId}/reactions`,
    );
  }

  async getReadingCircleLeaderboard(
    readingCircleId: string,
  ): Promise<ReadingCircleLeaderboard> {
    const response = await getApiInstance().get(
      `/circles/${readingCircleId}/leaderboard`,
    );

    return mapReadingCircleLeaderboard(response.data);
  }

  /**
   * Fetches peers (friends) from the user's reading circles
   * @returns Array of users that are in the same reading circles as the current user (only students)
   */
  async getReadingCirclePeers(): Promise<User[]> {
    const response = await getApiInstance().get('/circles/peers');
    // Filter the response to only include student users
    return response.data
      .map(mapUser)
      .filter((user: User) => user.type === 'student');
  }
}
